import { getEnvConfig } from '@hbs-us/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { apiFetchJson } from '../../lib/apiFetch.js';
import { getAppEnv } from '../../lib/env.js';
import type {
  SupportMessageDto,
  SupportMessagesResponse,
  SupportResponse,
  SupportSubmitInput,
  SupportSubmitResponse,
} from './contractsMirror.js';

/**
 * TanStack Query hooks for the coarse Support endpoint (CARVE-07). The SERVER owns the ticket
 * queue + status counts (22-07); the SPA renders the returned `SupportResponse` and submits a
 * new request via `POST /api/v1/support`.
 *
 * Both calls go through `apiFetch` (the only fetch path). The tenant is derived server-side
 * from the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant). The POST
 * rides apiFetch's double-submit CSRF on the non-safe method.
 */

export function supportQueryKey(): readonly unknown[] {
  return ['support', 'tickets'] as const;
}

export function supportMessagesQueryKey(ticketId: number): readonly unknown[] {
  return ['support', 'tickets', ticketId, 'messages'] as const;
}

export function useSupportQuery() {
  return useQuery<SupportResponse>({
    queryKey: supportQueryKey(),
    queryFn: () => apiFetchJson<SupportResponse>('/api/v1/support/tickets'),
  });
}

export function useSupportMessagesQuery(ticketId: number | null) {
  return useQuery<SupportMessagesResponse>({
    queryKey: ticketId
      ? supportMessagesQueryKey(ticketId)
      : ['support', 'tickets', 'none', 'messages'],
    enabled: ticketId !== null,
    queryFn: () =>
      apiFetchJson<SupportMessagesResponse>(`/api/v1/support/tickets/${ticketId}/messages`),
  });
}

export function useSupportMutation() {
  const queryClient = useQueryClient();

  return useMutation<SupportSubmitResponse, Error, SupportSubmitInput>({
    mutationFn: (input) =>
      // NO tenantId — the server resolves the tenant from the principal (SEC-11).
      apiFetchJson<SupportSubmitResponse>('/api/v1/support', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportQueryKey() });
    },
  });
}

interface SupportSocketFrame {
  type?: string;
  message?: SupportMessageDto;
  error?: string;
}

function supportWsUrl(ticket: string): string {
  const apiHost = getEnvConfig(getAppEnv()).domains.api;
  return `wss://${apiHost}/api/v1/support/ws?ticket=${encodeURIComponent(ticket)}`;
}

function appendMessage(
  current: SupportMessagesResponse | undefined,
  message: SupportMessageDto,
): SupportMessagesResponse {
  const messages = current?.messages ?? [];
  if (messages.some((m) => m.id === message.id)) return current ?? { ok: true, messages };
  return { ok: true, messages: [...messages, message] };
}

export function useSupportTicketSocket(ticketId: number | null) {
  const qc = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUnmountRef = useRef(false);

  useEffect(() => {
    if (ticketId === null) return;
    closedByUnmountRef.current = false;
    let cancelled = false;
    let attempts = 0;

    const scheduleReconnect = () => {
      if (cancelled || closedByUnmountRef.current) return;
      const delay = Math.min(1000 * 2 ** attempts, 15000);
      attempts += 1;
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    async function connect(): Promise<void> {
      try {
        const { ticket } = await apiFetchJson<{ ok: boolean; ticket?: string }>(
          `/api/v1/support/tickets/${ticketId}/ws-ticket`,
          { method: 'POST', body: JSON.stringify({}) },
        );
        if (cancelled || closedByUnmountRef.current || !ticket) {
          if (!ticket) scheduleReconnect();
          return;
        }

        const ws = new WebSocket(supportWsUrl(ticket));
        socketRef.current = ws;

        ws.addEventListener('open', () => {
          attempts = 0;
          setError(null);
          setIsOpen(true);
        });
        ws.addEventListener('message', (event) => {
          try {
            const frame = JSON.parse(String(event.data)) as SupportSocketFrame;
            if (frame.type === 'support.message' && frame.message?.ticketId === ticketId) {
              qc.setQueryData<SupportMessagesResponse>(
                supportMessagesQueryKey(ticketId),
                (current) => appendMessage(current, frame.message as SupportMessageDto),
              );
              void qc.invalidateQueries({ queryKey: supportQueryKey() });
            }
            if (frame.type === 'support.error') setError(frame.error ?? 'message_failed');
          } catch {
            // Ignore malformed frames; the HTTP query remains the source of truth.
          }
        });
        ws.addEventListener('close', () => {
          setIsOpen(false);
          scheduleReconnect();
        });
        ws.addEventListener('error', () => {
          setError('connection_failed');
          try {
            ws.close();
          } catch {
            // already closing
          }
        });
      } catch {
        setIsOpen(false);
        scheduleReconnect();
      }
    }

    void connect();

    return () => {
      cancelled = true;
      closedByUnmountRef.current = true;
      setIsOpen(false);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      const ws = socketRef.current;
      socketRef.current = null;
      if (ws) {
        try {
          ws.close();
        } catch {
          // already closed
        }
      }
    };
  }, [ticketId, qc]);

  const sendMessage = useCallback((body: string): boolean => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setError('not_connected');
      return false;
    }
    ws.send(JSON.stringify({ type: 'support.message.send', body }));
    return true;
  }, []);

  return { isOpen, error, sendMessage };
}
